/*
 * The Filter-Sparplan popup, KK-1124.
 *
 * This is the only thing in the theme that stands in front of an add to cart,
 * so it is written to be inert by default and to fail open. Every early return
 * below leaves the add exactly as it was before this file existed:
 *
 *   1. the popup is not on the page, because it is switched off or unconfigured
 *   2. the form carries no marker, so the product is not one we ask about
 *   3. the offer is already in the cart, so there is nothing to ask
 *   4. the frequency rule says this visitor has been asked already
 *   5. this file failed to load at all
 *
 * The offer itself is a row the customer picks, not a fixed one. The rows are
 * the shared subscription rows, so the popup asks the same question the product
 * page asks, and the chosen row carries both the pack and its plan.
 *
 * When it does intercept, the customer's own add is never reimplemented: it is
 * held, and then handed back to Horizon's form untouched, so the error
 * handling, the cart events and the drawer opening all stay where they are. The
 * only request this file makes is the one for the offer itself.
 */

const POPUP_ID = 'pp-subscription-popup';
const TRIGGER = 'data-pp-popup-trigger';
const STORAGE_KEY = 'pp-subscription-popup-asked';

/*
 * The row the customer chose. Matched by class rather than by control name,
 * because the name is written by Liquid and a second copy of it here would be
 * a second thing to keep in step. The row carries both halves of the offer: its
 * value is the plan, its data-variant-id is the pack that plan is allocated to.
 */
const CHOSEN = '.pp-subscription-popup__plans input:checked';

class PpSubscriptionPopup {
  /** @type {HTMLFormElement | null} The add being held while the question is asked. */
  #pending = null;

  /** @type {HTMLElement | null} The button that submitted it, kept so the release is identical. */
  #submitter = null;

  /** @type {HTMLFormElement | null} The form we are releasing on purpose, so we do not catch it again. */
  #bypass = null;

  /** Set while the offer is being taken, so closing the dialog does not release twice. */
  #accepting = false;

  /** The accept button, so its busy state can drive the spinner in its icon. */
  #accept = null;

  /** Nothing is held back until the dialog is able to open. */
  #ready = false;

  constructor() {
    this.root = document.getElementById(POPUP_ID);
    if (!this.root) return;

    /*
     * `showDialog` belongs to Horizon's `dialog-component`, which is a module
     * of its own and may upgrade after this one. Holding an add before that
     * happens would swallow it: the submit is already cancelled and the thing
     * meant to justify cancelling it does not exist yet.
     */
    customElements.whenDefined('dialog-component').then(() => {
      this.#ready = true;
    });

    /*
     * On window rather than on document, and this is not a detail.
     *
     * Horizon's own `on:` bindings are one delegated listener per event type,
     * registered on `document` with `capture: true` (assets/component.js). Two
     * capture listeners on the same node run in registration order, and
     * component.js is imported by the critical bundle, so it always registers
     * first. A capture listener of ours on `document` would therefore run
     * second, after `handleSubmit` had already sent the add.
     *
     * `window` sits one step earlier in the capture path, so this always runs
     * first, whatever the load order turns out to be.
     */
    window.addEventListener('submit', this.#onSubmit, true);

    /*
     * Three listeners for one thing, because losing a held add is the worst
     * thing this file can do and each of them covers a case the others do not.
     * Releasing is idempotent, so hearing all three costs nothing.
     *
     * The class watcher is the one that always fires, and it fires first.
     * `closeDialog` adds `dialog-closing` and then waits on the panel's
     * animations before it actually closes, so on the two paths that never
     * touch our own buttons, Escape and the click outside, the wait sits
     * between the customer's decision and their fountain reaching the cart.
     * A paused timeline, a backgrounded tab, would hold it there indefinitely.
     * Watching the class releases on the decision instead of on the animation.
     */
    const dialog = this.root.querySelector('dialog');

    if (dialog) {
      new MutationObserver(() => {
        if (dialog.classList.contains('dialog-closing')) this.#onDialogClose();
      }).observe(dialog, { attributes: true, attributeFilter: ['class'] });

      dialog.addEventListener('close', this.#onDialogClose);
    }

    this.root.addEventListener('dialog:close', this.#onDialogClose);
    this.#accept = this.root.querySelector('[data-pp-popup-accept]');
    this.#accept?.addEventListener('click', this.#onAccept);
    this.root.querySelector('[data-pp-popup-decline]')?.addEventListener('click', this.#onDecline);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Our own release, on its way back to Horizon.
    if (form === this.#bypass) return;

    // Anything inside the popup belongs to the popup.
    if (this.root.contains(form)) return;

    // The marker is written by Liquid, so the browser never carries a product
    // list and nothing has to be kept in step with the shop's collections.
    if (!form.closest(`[${TRIGGER}]`)) return;

    if (!this.#ready) return;
    if (this.root.dataset.taken === 'true') return;
    if (this.#asked()) return;

    // Past this line the add belongs to us until the question is answered.
    event.preventDefault();
    event.stopPropagation();

    this.#pending = form;
    this.#submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
    this.#accepting = false;

    this.#remember();

    try {
      this.root.showDialog();
    } catch (error) {
      // An add that was taken and then not asked about is an add that never
      // happened, which is the one outcome this file must never produce.
      console.error(error);
      this.#release();
    }
  };

  #onAccept = async () => {
    if (this.#accepting) return;
    this.#accepting = true;

    // The dialog stays open with the accept button showing a spinner while the
    // offer is added, so the wait is visible. aria-busy swaps the arrow for the
    // spinner (stylesheet); the #accepting guard already blocks a second click,
    // so the button is not disabled and keeps its full colour.
    this.#accept?.setAttribute('aria-busy', 'true');

    // The offer goes in first and is waited for, so that the add released
    // below is the one that re-renders the drawer and the customer sees both
    // lines at once rather than the fountain and then a jump.
    await this.#addOffer();

    this.#accept?.removeAttribute('aria-busy');
    this.root.closeDialog();

    // Whether the offer made it or not, the customer still gets what they
    // pressed add on.
    this.#release();
  };

  #onDecline = () => {
    this.root.closeDialog();
  };

  /*
   * Every way out that is not the accept button lands here: the decline link,
   * the close button, Escape and a click on the backdrop, because all four go
   * through `dialog-component`. All four mean the same thing. A customer who
   * pressed add to cart and got nothing would read it as a broken button.
   */
  #onDialogClose = () => {
    if (this.#accepting) return;
    this.#release();
  };

  #release() {
    const form = this.#pending;
    const submitter = this.#submitter;

    this.#pending = null;
    this.#submitter = null;

    if (!form?.isConnected) return;

    // requestSubmit fires the submit event synchronously, so the flag can be
    // cleared on the next line and cannot leak to any other form.
    this.#bypass = form;
    form.requestSubmit(submitter instanceof HTMLButtonElement ? submitter : undefined);
    this.#bypass = null;
  }

  async #addOffer() {
    const chosen = this.root.querySelector(CHOSEN);
    if (!(chosen instanceof HTMLInputElement)) return;

    // The pack, not the product: a plan is allocated to one variant and adding
    // it against another is the fault the buy box was already fixed for.
    const variantId = Number(chosen.dataset.variantId);
    if (!variantId) return;

    const line = { id: variantId, quantity: 1 };

    // An empty value means a row with no plan. The popup does not draw one, so
    // this is here for the day someone turns that row on rather than for today.
    const planId = Number(chosen.value);
    if (planId) line.selling_plan = planId;

    try {
      /*
       * No `.js` appended, unlike every other cart call in this theme.
       * Horizon's route table is not consistent about it: `cart_change_url` is
       * `/cart/change` and takes the suffix, `cart_add_url` is already
       * `/cart/add.js` and appending gives `/cart/add.js.js`, which answers 404
       * with an empty body and no error anywhere.
       */
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [line] }),
      });

      // Taken is set from the answer rather than from the attempt, so a refused
      // add leaves the offer open to be made again.
      if (response.ok) this.root.dataset.taken = 'true';
    } catch {
      // The offer is the extra, not the errand. A failed add here is silent by
      // design: the customer asked for a fountain and is about to get one.
    }
  }

  /** @returns {boolean} */
  #asked() {
    const store = this.#store();
    if (!store) return false;

    try {
      return store.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  #remember() {
    const store = this.#store();
    if (!store) return;

    try {
      store.setItem(STORAGE_KEY, 'true');
    } catch {
      // Private browsing refuses to write. Asking again is the harmless half of
      // this, so nothing else is done about it.
    }
  }

  /** @returns {Storage | null} */
  #store() {
    switch (this.root.dataset.frequency) {
      case 'session':
        return window.sessionStorage;
      case 'visitor':
        return window.localStorage;
      default:
        return null;
    }
  }
}

const start = () => new PpSubscriptionPopup();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
